<script setup lang="ts">
  import { computed } from 'vue';
  import type { FormElement, RadioGroupProperties } from '../../../model/form.types';
  import { useLabelPosition } from '../../../model/useLabelPosition';
  import { useElementContainerStyles } from '../../../model/useElementContainerStyles';
  import { useFormBuilderForcedReadonly } from '../../../model/useFormBuilderReadonly';

  interface Props {
    element: FormElement & { properties: RadioGroupProperties };
  }

  const props = defineProps<Props>();

  const isForcedReadonly = useFormBuilderForcedReadonly();

  const { isLabelLeft, labelClasses, labelStyles } = useLabelPosition(computed(() => props.element.properties));

  const { containerStyles } = useElementContainerStyles(
    computed(() => props.element.properties),
    'RADIO_GROUP'
  );

  const options = computed(() => props.element.properties.options ?? []);

  const radioGroupName = computed(() => `radio-${props.element.id}`);

  const labelId = computed(() => `label-${props.element.id}`);

  const isHorizontal = computed(() => props.element.properties.orientation === 'horizontal');

  const optionLabelStyles = computed(() => {
    const p = props.element.properties;
    const styles: Record<string, string> = {
      fontSize: p.optionFontSize ? `${p.optionFontSize}px` : '10px',
      lineHeight: '1',
    };
    if (p.optionFontWeight === 'bold') styles.fontWeight = '700';
    else if (p.optionFontWeight === 'semibold') styles.fontWeight = '600';
    else if (p.optionFontWeight === 'normal') styles.fontWeight = '400';
    if (p.optionFontStyle === 'italic') styles.fontStyle = 'italic';
    if (p.optionTextDecoration && p.optionTextDecoration !== 'none') {
      styles.textDecoration = p.optionTextDecoration;
      styles.textDecorationSkipInk = 'none';
    }
    if (p.optionTextAlign) styles.textAlign = p.optionTextAlign;
    if (p.optionColor) styles.color = p.optionColor;
    return styles;
  });
</script>

<template>
  <div
    class="radio-group-element"
    :class="{ 'label-left': isLabelLeft }"
    :style="containerStyles"
  >
    <div
      v-if="isLabelLeft"
      class="label-container"
    >
      <label
        v-if="element.properties.label"
        :id="labelId"
        :class="labelClasses"
        :style="labelStyles"
      >
        {{ element.properties.label }}
      </label>
      <p
        v-if="element.properties.helperText"
        class="text-[10px] text-gray-500 mt-1"
      >
        {{ element.properties.helperText }}
      </p>
    </div>

    <template v-else>
      <label
        v-if="element.properties.label"
        :id="labelId"
        :class="labelClasses"
        class="block mb-2"
        :style="labelStyles"
      >
        {{ element.properties.label }}
        <span
          v-if="element.properties.helperText"
          class="text-[10px] text-gray-500 font-normal ml-1"
        >
          ({{ element.properties.helperText }})
        </span>
      </label>
    </template>

    <div
      class="radio-options"
      role="radiogroup"
      :aria-labelledby="element.properties.label ? labelId : undefined"
      aria-disabled="true"
      :class="{
        'ml-4': isLabelLeft,
        'radio-options--horizontal': isHorizontal,
        'radio-options--vertical': !isHorizontal,
      }"
    >
      <div
        v-for="(option, index) in options"
        :key="index"
        class="radio-option-item"
      >
        <label class="inline-flex items-center cursor-pointer">
          <input
            type="radio"
            :name="radioGroupName"
            :value="option"
            :disabled="isForcedReadonly"
            class="hidden"
          >
          <span class="inline-block rounded-full border border-light-gray-xx relative w-3.5 h-3.5" />
          <span
            class="ml-1.5 select-none text-gray-07 font-medium text-left"
            :style="optionLabelStyles"
          >{{ option }}</span>
        </label>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .radio-group-element {
    font-family: var(--font-base);
    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }

  .radio-group-element.label-left {
    display: flex;
    align-items: flex-start;
    min-width: 0;
  }

  .radio-group-element.label-left .label-container {
    flex-shrink: 0;
    margin-right: 0.5rem;
  }

  .radio-group-element.label-left .label-container label {
    display: block;
    white-space: nowrap;
  }

  .radio-group-element.label-left .radio-options {
    flex: 1;
    min-width: 0;
  }

  .radio-options--vertical .radio-option-item {
    margin-bottom: 0.25rem;
  }

  .radio-options--vertical .radio-option-item:last-child {
    margin-bottom: 0;
  }

  .radio-options--horizontal {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .radio-options--horizontal .radio-option-item {
    margin-bottom: 0;
  }

  .radio-options {
    pointer-events: none;
    user-select: none;
  }

  .radio-option-item label {
    line-height: 1;
  }
</style>
